import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import type { IdentifierDefinitionTypeCode } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type {
  AdminIdentifierTypeViewModel,
  UpdateAdminIdentifierTypeInput,
} from '../api';

interface DraftState {
  userEnteredId: string;
  label: string;
  type: IdentifierDefinitionTypeCode;
  order: string;
  prefix: string;
  format: string;
  formatHint: string;
  enforceFormat: boolean;
  allowDuplicates: boolean;
  autoGenerate: boolean;
  isPrimary: boolean;
  active: boolean;
}

interface FieldErrors {
  userEnteredId?: string;
  label?: string;
}

function draftFrom(row: AdminIdentifierTypeViewModel | null): DraftState {
  return {
    userEnteredId: row?.userEnteredId ?? '',
    label: row?.label ?? '',
    type: (row?.type as IdentifierDefinitionTypeCode | null) ?? 'alphanumeric',
    order: row?.order !== null && row?.order !== undefined ? String(row.order) : '',
    prefix: row?.prefix ?? '',
    format: row?.format ?? '',
    formatHint: row?.formatHint ?? '',
    enforceFormat: row?.enforceFormat ?? false,
    allowDuplicates: row?.allowDuplicates ?? false,
    autoGenerate: row?.autoGenerate ?? false,
    isPrimary: row?.isPrimary ?? false,
    active: row?.active ?? true,
  };
}

export function EditIdentifierTypeDialog({
  row,
  onCancel,
  onSubmit,
  pending,
  submitError,
}: {
  readonly row: AdminIdentifierTypeViewModel | null;
  readonly onCancel: () => void;
  readonly onSubmit: (input: UpdateAdminIdentifierTypeInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = row !== null;
  const [draft, setDraft] = useState<DraftState>(draftFrom(row));
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (row !== null) {
      setDraft(draftFrom(row));
      setErrors({});
    }
  }, [row]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (row === null) return;
    const next: FieldErrors = {};
    const userEnteredId = draft.userEnteredId.trim();
    const label = draft.label.trim();
    if (userEnteredId.length === 0) {
      next.userEnteredId = t('routes.admin.general.identifiers.create.errors.userEnteredIdRequired');
    } else if (userEnteredId.length > 255) {
      next.userEnteredId = t('routes.admin.general.identifiers.create.errors.userEnteredIdTooLong');
    }
    if (label.length === 0) {
      next.label = t('routes.admin.general.identifiers.create.errors.labelRequired');
    } else if (label.length > 255) {
      next.label = t('routes.admin.general.identifiers.create.errors.labelTooLong');
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    const prefix = draft.prefix.trim();
    const format = draft.format.trim();
    const formatHint = draft.formatHint.trim();
    const orderTrimmed = draft.order.trim();
    const orderParsed = orderTrimmed.length > 0 ? Number(orderTrimmed) : null;
    onSubmit({
      id: row.id,
      userEnteredId,
      label,
      type: draft.type,
      order: orderParsed === null || Number.isNaN(orderParsed) ? null : orderParsed,
      prefix: prefix.length > 0 ? prefix : null,
      format: format.length > 0 ? format : null,
      formatHint: formatHint.length > 0 ? formatHint : null,
      enforceFormat: draft.enforceFormat,
      allowDuplicates: draft.allowDuplicates,
      autoGenerate: draft.autoGenerate,
      isPrimary: draft.isPrimary,
      active: draft.active,
    });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-2xl" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.general.identifiers.edit.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <FormField
              id="admin-identifier-type-edit-userEnteredId"
              label={t('routes.admin.general.identifiers.create.fields.userEnteredId')}
              required
              error={errors.userEnteredId}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.userEnteredId}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, userEnteredId: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="admin-identifier-type-edit-label"
              label={t('routes.admin.general.identifiers.create.fields.label')}
              required
              error={errors.label}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.label}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, label: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </FormField>
            <FormField
              id="admin-identifier-type-edit-type"
              label={t('routes.admin.general.identifiers.create.fields.type')}
            >
              {(fp) => (
                <Select
                  {...fp}
                  value={draft.type}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, type: e.target.value as IdentifierDefinitionTypeCode }));
                  }}
                >
                  <option value="alphanumeric">
                    {t('routes.admin.general.identifiers.create.fields.typeAlphanumeric')}
                  </option>
                  <option value="numeric">
                    {t('routes.admin.general.identifiers.create.fields.typeNumeric')}
                  </option>
                </Select>
              )}
            </FormField>
            <FormField
              id="admin-identifier-type-edit-order"
              label={t('routes.admin.general.identifiers.create.fields.order')}
            >
              {(fp) => (
                <Input
                  {...fp}
                  type="number"
                  inputMode="numeric"
                  value={draft.order}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, order: e.target.value }));
                  }}
                />
              )}
            </FormField>
            <FormField
              id="admin-identifier-type-edit-prefix"
              label={t('routes.admin.general.identifiers.create.fields.prefix')}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.prefix}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, prefix: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="admin-identifier-type-edit-format"
              label={t('routes.admin.general.identifiers.create.fields.format')}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.format}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, format: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="admin-identifier-type-edit-formatHint"
              label={t('routes.admin.general.identifiers.create.fields.formatHint')}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.formatHint}
                  onChange={(e) => {
                    setDraft((p) => ({ ...p, formatHint: e.target.value }));
                  }}
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2 border-t border-border pt-4 sm:grid-cols-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.enforceFormat}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, enforceFormat: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.general.identifiers.create.fields.enforceFormat')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.allowDuplicates}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, allowDuplicates: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.general.identifiers.create.fields.allowDuplicates')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.autoGenerate}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, autoGenerate: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.general.identifiers.create.fields.autoGenerate')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isPrimary}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, isPrimary: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.general.identifiers.create.fields.isPrimary')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, active: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.general.identifiers.create.fields.active')}
            </label>
          </div>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.general.identifiers.edit.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
              {t('routes.admin.general.identifiers.edit.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {t('routes.admin.general.identifiers.edit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
